import type { ExchangeRate, ExchangeRateProvider, RateDirection } from './types'

interface BoardRateRow {
  code: string
  name: string
  alis: number | null
  satis: number | null
  alis_dir?: string
  satis_dir?: string
  updated_at?: string | null
  tarih?: string | null
}

interface BoardConfigItem {
  code: string
  name: string
  category: string
  sort_order: number
  show_on_tv: boolean
  margin_type: string
  margin_value: number
}

interface BoardRatesResponse {
  ok: boolean
  server_time?: string
  groups?: Record<string, BoardRateRow[]>
  error?: string
}

interface BoardConfigResponse {
  ok: boolean
  items?: Record<string, BoardConfigItem>
  preview_mode?: boolean
  error?: string
}

const FX_CODE_MAP: Record<string, string> = {
  USDTRY: 'USD',
  EURTRY: 'EUR',
  GBPTRY: 'GBP',
  CHFTRY: 'CHF',
  JPYTRY: 'JPY',
  SARTRY: 'SAR',
  AUDTRY: 'AUD',
  CADTRY: 'CAD',
}

const GOLD_CATEGORIES = new Set(['altin', 'sarrafiye', 'maden'])

function mapDirection(raw?: string): RateDirection {
  if (raw === 'up') return 'up'
  if (raw === 'down') return 'down'
  return 'neutral'
}

function applyMargin(
  alis: number | null,
  satis: number | null,
  type: string,
  value: number,
): { buy: number | null; sell: number | null } {
  const t = type || 'none'
  const v = Number(value) || 0
  if (t === 'none' || v === 0) return { buy: alis, sell: satis }
  if (t === 'percent') {
    const f = v / 100
    return {
      buy: alis != null ? Math.round(alis * (1 - f) * 10000) / 10000 : null,
      sell: satis != null ? Math.round(satis * (1 + f) * 10000) / 10000 : null,
    }
  }
  return {
    buy: alis != null ? Math.round((alis - v) * 10000) / 10000 : null,
    sell: satis != null ? Math.round((satis + v) * 10000) / 10000 : null,
  }
}

function displayCode(source: string): string {
  if (FX_CODE_MAP[source]) return FX_CODE_MAP[source]
  if (source === 'ALTIN' || source === 'KULCEALTIN') return 'ALTIN'
  if (source.startsWith('CEYREK')) return source.includes('ESKI') ? 'CEYREK_ESKI' : 'CEYREK'
  return source
}

function toCategory(boardCategory: string): 'fx' | 'gold' {
  if (boardCategory === 'doviz' || boardCategory === 'parite') return 'fx'
  if (GOLD_CATEGORIES.has(boardCategory)) return 'gold'
  return 'fx'
}

/**
 * Kiox / TV panosu ile aynı kaynak:
 * - GET /api/rates.php
 * - GET /api/board-config.php (margin + show_on_tv)
 */
export class ApiExchangeRateProvider implements ExchangeRateProvider {
  private readonly ratesUrl: string
  private readonly configUrl: string

  constructor(
    ratesUrl = import.meta.env.VITE_RATES_API_URL ?? '/api/rates.php',
    configUrl = import.meta.env.VITE_BOARD_CONFIG_URL ?? '/api/board-config.php',
  ) {
    this.ratesUrl = ratesUrl
    this.configUrl = configUrl
  }

  async getRates(): Promise<ExchangeRate[]> {
    const [ratesRes, configRes] = await Promise.all([
      fetch(this.ratesUrl, { cache: 'no-store' }),
      fetch(this.configUrl, { cache: 'no-store' }),
    ])

    if (!ratesRes.ok) throw new Error(`Rates API ${ratesRes.status}`)

    const ratesPayload = (await ratesRes.json()) as BoardRatesResponse
    if (!ratesPayload.ok || !ratesPayload.groups) {
      throw new Error(ratesPayload.error ?? 'Kur yanıtı geçersiz')
    }

    let configItems: Record<string, BoardConfigItem> = {}
    let previewMode = false
    if (configRes.ok) {
      const cfg = (await configRes.json()) as BoardConfigResponse
      if (cfg.ok && cfg.items) configItems = cfg.items
      previewMode = !!cfg.preview_mode
    }

    const byCode = new Map<string, BoardRateRow>()
    for (const rows of Object.values(ratesPayload.groups)) {
      for (const row of rows) byCode.set(row.code, row)
    }

    const fallbackNow = ratesPayload.server_time ?? new Date().toISOString()
    const result: ExchangeRate[] = []

    // Config varsa panodaki gibi: sadece show_on_tv + sıralı
    const configList = Object.values(configItems)
    if (configList.length > 0) {
      const visible = configList
        .filter((c) => c.show_on_tv)
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'tr'))

      for (const cfg of visible) {
        const row = byCode.get(cfg.code)
        if (!row) continue
        const mapped = mapRow(row, cfg, fallbackNow, previewMode)
        if (mapped) result.push(mapped)
      }
      return result
    }

    // Config yoksa rates groups'u düzleştir
    for (const [groupKey, rows] of Object.entries(ratesPayload.groups)) {
      for (const row of rows) {
        const mapped = mapRow(
          row,
          {
            code: row.code,
            name: row.name,
            category: groupKey,
            sort_order: 0,
            show_on_tv: true,
            margin_type: 'none',
            margin_value: 0,
          },
          fallbackNow,
          previewMode,
        )
        if (mapped) result.push(mapped)
      }
    }

    return result
  }
}

function mapRow(
  row: BoardRateRow,
  cfg: BoardConfigItem,
  fallbackNow: string,
  previewMode = false,
): ExchangeRate | null {
  if (previewMode) {
    return {
      code: displayCode(row.code),
      sourceCode: row.code,
      name: cfg.name || row.name || row.code,
      buy: 0,
      sell: 0,
      direction: 'neutral',
      updatedAt: row.updated_at || fallbackNow,
      category: toCategory(cfg.category),
    }
  }

  const { buy, sell } = applyMargin(row.alis, row.satis, cfg.margin_type, cfg.margin_value)
  if (buy == null && sell == null) return null

  const buySafe = buy ?? sell ?? 0
  const sellSafe = sell ?? buy ?? 0
  const dir = mapDirection(row.alis_dir || row.satis_dir)

  return {
    code: displayCode(row.code),
    sourceCode: row.code,
    name: cfg.name || row.name || row.code,
    buy: buySafe,
    sell: sellSafe,
    direction: dir,
    updatedAt: row.updated_at || fallbackNow,
    category: toCategory(cfg.category),
  }
}
